import { useEffect, useCallback, useRef } from "react";
import { mainBus, metricsBus } from "@/common/buses";
import { useLocation } from "react-router-dom";
import { useAppStore } from "@/common/store/appStore";
import { useBodyLock } from "@/common/hooks/useBodyLock";
import { useScrollStore } from "@/common/store/scrollStore";
import { useVideoQueueStore } from "@/common/store/videoQueueStore";

export const useFullScreen = () => {
  const isFullScreen = useAppStore((state) => state.isFullScreen);
  const toggleFullScreen = useAppStore((state) => state.toggleFullScreen);
  const playingGif = useRef<string|null>(null);
  const scrollElement = useScrollStore((state) => state.scrollElement);
  const videoQueue = useVideoQueueStore((state) => state.videoQueue);
  const location = useLocation();
  const getLastVisibleVideo = useVideoQueueStore((s) => s.getLastVisibleVideo);

  const scrollIntoView = useCallback(() => {
    if (playingGif.current === null) {
      scrollElement?.scrollTo({ top: 0 });
      return;
    }

    setTimeout(() => document.querySelector(`#${playingGif.current}`)?.scrollIntoView({ behavior: "auto", block: "center" }), 1);
  }, []);

  useEffect(() => {
    const handleKeyDown = (event: KeyboardEvent) => {
      if (!isFullScreen) {
        return;
      }

      if (playingGif.current === null) {
        return;
      }

      const gifElement = document.querySelector<HTMLDivElement>(`#${playingGif.current}`);

      if (!gifElement) {
        return;
      }

      const parent = gifElement.parentElement;

      if (!parent) {
        return;
      }

      switch (event.key) {

      case "Escape":
        event.preventDefault();
        mainBus.emit("toggle_fullscreen");
        break;

      case "ArrowDown":
        event.preventDefault();
        parent.scrollBy({
          top: window.innerHeight,
          behavior: "smooth",
        });
        break;

      case "ArrowUp":
        event.preventDefault();
        parent.scrollBy({
          top: -window.innerHeight,
          behavior: "smooth",
        });
        break;

      default:
        break;
      }
    };

    window.addEventListener("keydown", handleKeyDown);

    return () => {
      window.removeEventListener("keydown", handleKeyDown);
    };
  }, [isFullScreen, toggleFullScreen, videoQueue]);

  useEffect(() => {
    return () => {
      toggleFullScreen(false);
    };
  }, [toggleFullScreen, location]);

  useEffect(() => {
    const toggleFullscreen = (payload?: {id?: string}) => {
      const requestedId = payload?.id;
      const lastPlayingId = requestedId ?? getLastVisibleVideo();

      playingGif.current = lastPlayingId ? `gif_${lastPlayingId}` : null;

      if (isFullScreen) {
        toggleFullScreen(false);
        metricsBus.emit("forceFullscreenExited");
      } else {
        toggleFullScreen(!isFullScreen);
      }

      scrollIntoView();
    };

    mainBus.on("toggle_fullscreen", toggleFullscreen);

    return () => {
      mainBus.off("toggle_fullscreen", toggleFullscreen);
    };
  }, [isFullScreen, toggleFullScreen, getLastVisibleVideo]);

  useBodyLock(isFullScreen);
};
